/* eslint-disable */
//  src/layouts/allVisitTransactions/AllVisitTransactions.js

import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import { Box, Button, TextField } from "@mui/material";
import AppSnackbar from "components/common/AppSnackbar";
import VisitTransactionService from "services/visitTransactionService";
import VisitTransactionsView from "components/VisitTransactionDetails/visitTransactionsView";
import useDebouncedValue from "hooks/useDebouncedValue";
import { useLocation } from "react-router-dom";
import { filterApplyButtonSx, filterClearButtonSx } from "components/common/buttonStyles";

const defaultFilters = {
  from: "",
  to: "",
};

const defaultPagination = {
  page: 1,
  pageSize: 25,
  totalRows: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

export default function AllVisitTransactions() {
  const location = useLocation();
  const initialViewState = location.state?.viewState || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "warning" });

  const restoredFilters = useMemo(
    () => ({
      from: initialViewState.filters?.from || "",
      to: initialViewState.filters?.to || "",
    }),
    [initialViewState.filters?.from, initialViewState.filters?.to]
  );

  const [sortBy] = useState("date");
  const [searchText, setSearchText] = useState(initialViewState.searchText || initialViewState.query || "");
  const [appliedQuery, setAppliedQuery] = useState(initialViewState.query || "");
  const [filters, setFilters] = useState(restoredFilters);
  const [appliedFilters, setAppliedFilters] = useState(restoredFilters);
  const [page, setPage] = useState(initialViewState.page || 1);
  const [pageSize, setPageSize] = useState(initialViewState.pageSize || 25);
  const [pagination, setPagination] = useState({
    ...defaultPagination,
    page: initialViewState.page || 1,
    pageSize: initialViewState.pageSize || 25,
  });
  const debouncedSearchText = useDebouncedValue(searchText, 500);

  useEffect(() => {
    const viewState = location.state?.viewState;
    if (!viewState) return;
    setSearchText(viewState.searchText || viewState.query || "");
    setAppliedQuery(viewState.query || "");
    setFilters({
      from: viewState.filters?.from || "",
      to: viewState.filters?.to || "",
    });
    setAppliedFilters({
      from: viewState.filters?.from || "",
      to: viewState.filters?.to || "",
    });
    setPage(viewState.page || 1);
    setPageSize(viewState.pageSize || 25);
  }, [location.key, location.state]);

  useEffect(() => {
    const nextAppliedQuery = (debouncedSearchText || "").trim();
    if (nextAppliedQuery === appliedQuery) return;
    setAppliedQuery(nextAppliedQuery);
    setPage(1);
  }, [debouncedSearchText, appliedQuery]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await VisitTransactionService.getAllVisitTransactions({
          sortBy: "date",
          sortDir: "DESC",
          from: appliedFilters.from || undefined,
          to: appliedFilters.to || undefined,
          query: appliedQuery || undefined,
          page,
          pageSize,
        });
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setPagination(data?.pagination || { ...defaultPagination, page, pageSize });
      } catch (err) {
        console.error(err);
        setRows([]);
        setPagination({ ...defaultPagination, page, pageSize });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appliedFilters.from, appliedFilters.to, appliedQuery, page, pageSize]);

  const pageTitle = "Ingresos generales de todos los locales";
  const hasAppliedDateFilters = Boolean(appliedFilters.from || appliedFilters.to);
  const hasDraftDateFilters = Boolean(filters.from || filters.to);
  const filtersChanged =
    filters.from !== appliedFilters.from ||
    filters.to !== appliedFilters.to;
  const shouldShowClearFilters = hasAppliedDateFilters && !filtersChanged;
  const filtersButtonLabel = shouldShowClearFilters ? "Limpiar filtros" : "Aplicar filtros";
  const isFiltersButtonDisabled = !shouldShowClearFilters && !hasDraftDateFilters;
  const emptyMessage = appliedQuery
    ? "No hay resultados para la búsqueda."
    : hasAppliedDateFilters
      ? "No hay ingresos en el rango seleccionado."
      : "No hay ingresos registrados.";

  const handleFilterChange = (field) => (event) => {
    const nextValue = event.target.value;
    setFilters((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleApplyFilters = () => {
    if (filters.from && filters.to && filters.from > filters.to) {
      setSnack({
        open: true,
        message: "La fecha Desde no puede ser mayor que Hasta.",
        severity: "warning",
      });
      return;
    }

    setAppliedFilters({
      from: filters.from || "",
      to: filters.to || "",
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const handlePageChange = (nextPage) => {
    if (!Number.isInteger(nextPage) || nextPage < 1) return;
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              {pageTitle}
            </MDTypography>
          </MDBox>

          <MDBox pt={3} px={2} pb={2}>
            <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  columnGap: 3,
                  rowGap: 2,
                  p: 2,
                  mb: 2,
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    flex: "1 1 520px",
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label="Desde"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filters.from}
                    onChange={handleFilterChange("from")}
                    sx={{ minWidth: { xs: "100%", sm: 170 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                  />
                  <TextField
                    label="Hasta"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={filters.to}
                    onChange={handleFilterChange("to")}
                    sx={{ minWidth: { xs: "100%", sm: 170 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
                  />
                  <Button
                    variant={shouldShowClearFilters ? "outlined" : "contained"}
                    color="info"
                    disabled={isFiltersButtonDisabled}
                    sx={shouldShowClearFilters ? filterClearButtonSx : filterApplyButtonSx}
                    onClick={shouldShowClearFilters ? handleClearFilters : handleApplyFilters}
                  >
                    {filtersButtonLabel}
                  </Button>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end", flex: "0 1 260px", ml: "auto", minWidth: 0 }}>
                  <TextField
                    size="small"
                    label="Buscar por local, dueño o ciudad"
                    placeholder="Nombre del local, dueño o ciudad..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    sx={{ width: "100%", maxWidth: 260, minWidth: 0, "& .MuiInputBase-root": { height: 40 } }}
                  />
                </Box>
            </Box>

            <VisitTransactionsView
              rows={rows}
              loading={loading}
              showTitle={false}
              counterNoun="resultados"
              returnToLabel="Volver a ingresos general"
              emptyMessage={emptyMessage}
              sortBy="date"
              sortDir="DESC"
              onSortByChange={() => {}}
              onSortDirChange={() => {}}
              query={appliedQuery}
              onQueryChange={() => {}}
              filters={appliedFilters}
              page={page}
              pageSize={pageSize}
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              searchText={searchText}
              onSearchTextChange={setSearchText}
              appliedQuery={appliedQuery}
              serverSide={true}
              showSearch={false}
              showSortControls={false}
              showLocalColumn={true}
              sortOptions={[{ value: "date", label: "Fecha" }]}
            />
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
      <AppSnackbar
        open={snack.open}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        severity={snack.severity}
        message={snack.message}
      />
    </DashboardLayout>
  );
}
